// import $ from 'jquery';

// Shoutout AngusCroll (https://goo.gl/pxwQGp)
export const toType = (obj) => {
	if (obj === null || obj === undefined) {
		return `${obj}`;
	}

	return Object.prototype.toString.call(obj).match(/\s([a-z]+)/i)[1].toLowerCase();
};

/**
 * @param {string?} val
 */
export const normalizeData = (val) => {

	if (val === 'true' || val === '') {
		return true;
	}

	if (val === 'false') {
		return false;
	}

	if (val === Number(val).toString()) {
		return Number(val);
	}

	if (val === 'null') {
		return null;
	}

	try {
		return JSON.parse(val);
	}
	// not json
	catch {
		return val;
	}
};

/**
 * @param {HTMLElement} element
 * @param {string} prefix
 */
export const getOptionsFromDataset = (element, prefix = '', processResponsive = false) => {

	// No element/prefix/dataset
	if (!element || !prefix || !element.dataset) {
		return {};
	}

	// No camel-case prefix
	if (prefix.includes('-')) {
		prefix = mpro.camelize(prefix);
	}

	let options = Object.create(null);
	let optionsDesktop;
	let optionsMobile;

	if (processResponsive) {
		if (mpro.isMobileScreen()) {
			optionsMobile = Object.create(null);
		}
		else {
			optionsDesktop = Object.create(null);
		}
	}

	for (const name of Object.keys(element.dataset)) {
		if ((name !== prefix) && name.startsWith(prefix)) {
			let prop = name.slice(prefix.length);
			prop = prop[0].toLowerCase() + prop.slice(1);

			// Use the mobile option instead
			if (processResponsive && /^mobile[\dA-Z]/.test(prop)) {
				if (optionsMobile) {
					prop = prop.slice(6);
					prop = prop[0].toLowerCase() + prop.slice(1);
					optionsMobile[prop] = normalizeData(element.dataset[name]);
				}
			}
			// Use the desktop option instead
			else if (processResponsive && /^desktop[\dA-Z]/.test(prop)) {
				if (optionsDesktop) {
					prop = prop.slice(7);
					prop = prop[0].toLowerCase() + prop.slice(1);
					optionsDesktop[prop] = normalizeData(element.dataset[name]);
				}
			}
			// Use the default option
			else {
				options[prop] = normalizeData(element.dataset[name]);
			}
		}
	}

	// Mobile options take precedence
	if (optionsMobile) {
		options = { ...options, ...optionsMobile };
	}
	// Desktop options take precedence
	else if (optionsDesktop) {
		options = { ...options, ...optionsDesktop };
	}

	return options;
};

/**
 * @param {Document} startDoc
 */
export const getRoot = (startDoc) => {
	let doc = startDoc;
	let frame = getFrameElement(doc);
	while (frame) {
		doc = frame.ownerDocument;
		frame = getFrameElement(doc);
	}
	return doc;
};

let rootDocument;

export const getRootDocument = () => (rootDocument || (rootDocument = getRoot(document)));

/**
 * Returns the embedding frame element, if any.
 * @param {Document} doc
 */
export const getFrameElement = (doc) => {
	try {
		return doc.defaultView && doc.defaultView.frameElement || null;
	}
	catch {
		// Ignore the error.
		return null;
	}
};

/**
 * Checks to see if a parent element contains a child element (including inside
 * shadow DOM).
 * @param {Node} parent The parent element.
 * @param {Node} child The child element.
 * @return {boolean} True if the parent node contains the child node.
 */
export const containsDeep = (parent, child) => {
	let node = child;
	while (node) {
		if (node === parent) {
			return true;
		}
		node = getParentNode(node);
	}
	return false;
};

/**
 * Gets the parent node of an element or its host element if the parent node
 * is a shadow root.
 * @param {Node} node The node whose parent to get.
 * @return {Node|null} The parent node or null if no parent exists.
 */
export const getParentNode = (node) => {

	if (node.nodeType === 9 /* Node.DOCUMENT_NODE */ && node !== getRootDocument()) {
		// If this node is a document node, look for the embedding frame.
		// @ts-ignore
		return getFrameElement(node);
	}

	let parent = node.parentNode;

	// If the parent has element that is assigned through shadow root slot
	// @ts-ignore
	if (parent && parent.assignedSlot) {
		// @ts-ignore
		parent = parent.assignedSlot.parentNode;
	}

	// @ts-ignore
	if (parent && parent.nodeType === 11 /* Node.DOCUMENT_FRAGMENT_NODE */ && parent.host) {
		// If the parent is a shadow root, return the host element.
		// @ts-ignore
		return parent.host;
	}

	return parent;
};

/**
 * Returns true if `node` is a Document.
 * @param {!Node} node
 * @returns {boolean}
 */
export const isDoc = node => node && node.nodeType === Node.DOCUMENT_NODE;

/**
 * @param {string|number} value
 */
export const getCssSizeValue = (value) => {
	if (value) {
		// eslint-disable-next-line eqeqeq
		if (Number(value) == value) {
			return value + 'px';
		}
		// Return value as is
		return value;
	}
	return null;
};

/**
 * @param {string} name
 * @param {string} defaultValue
 */
export function getCssVar(name, defaultValue = '') {
	return String(window.getComputedStyle(document.documentElement).getPropertyValue((name.startsWith('--') ? '' : '--') + name) ?? defaultValue).trim();
}

/**
 * @param {*} obj
 */
export const isElement = (obj) => {
	if (!obj || typeof obj !== 'object') {
		return false;
	}

	if (typeof obj.jquery !== 'undefined') {
		obj = obj[0];
	}

	return typeof obj.nodeType !== 'undefined';
};

/**
 * @param {*} obj
 * @returns {HTMLElement?}
 */
export const getElement = (obj) => {
	if (isElement(obj)) { // it's a jQuery object or a node element
		return obj.jquery ? obj[0] : obj;
	}

	if (typeof obj === 'string' && obj.length > 0) {
		return document.querySelector(obj);
	}

	return null;
};

/** @type {string} */
let focusableSelector;

export const getFocusableSelector = () => {
	return focusableSelector || (focusableSelector = [
		'a',
		'button',
		'input',
		'textarea',
		'select',
		'details',
		'[tabindex]',
		'[contenteditable="true"]',
	].map(selector => `${selector}:not([tabindex^="-"])`).join(', '));
};

/**
 * @param {HTMLElement} element
 */
export const isFocusable = (element) => {
	return element.matches(getFocusableSelector()) && !isElementDisabled(element) && isElementDisplayed(element);
};

/**
 * @param {HTMLElement} element
 * @param {string=} filterSelector
 * @returns {HTMLElement[]}
 */
export const getFocusableChildren = (element, filterSelector) => {

	/** @type {HTMLElement[]} */ // @ts-ignore
	const collection = [...element.querySelectorAll(getFocusableSelector())];

	return collection.filter((el) => {
		if (filterSelector && !el.matches(filterSelector)) {
			return false;
		}
		return !isElementDisabled(el) && isElementDisplayed(el);
	});
};

const scrollableOverflowValues = new Set(['auto', 'scroll']);

/**
 * @param {(HTMLElement | Document | Window)=} element
 *
 * @returns {HTMLElement}
 */
export const getScrollParent = (element) => {

	if (!element || element === document || element === self || element === document.scrollingElement) {
		// @ts-ignore
		return document.scrollingElement || document.documentElement;
	}

	/** @type {HTMLElement} */ // @ts-ignore
	const parent = element.parentElement;

	// @ts-ignore
	return parent && (parent.scrollHeight >= parent.clientHeight) && scrollableOverflowValues.has(window.getComputedStyle(parent).overflowY)
		? parent
		: getScrollParent(parent);
};

/**
 * Check wether an element is in viewport
 *
 * @param {Partial<HTMLElement>} element
 * @param {number} threshold
 */
export const isElementInViewport = (element, threshold = 0) => {
	const rect = element.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		(rect.bottom - threshold) <= (window.innerHeight || document.documentElement.clientHeight) &&
		(rect.right - threshold) <= (window.innerWidth || document.documentElement.clientWidth)
	);
};

/**
 * Check wether an element is fully in viewport
 *
 * @param {Partial<HTMLElement>} el
 */
export const isElementFullyInViewport = (el) => {
	const width = el.offsetWidth;
	const height = el.offsetHeight;
	let top = el.offsetTop;
	let left = el.offsetLeft;

	while (el.offsetParent) {
		el = el.offsetParent;
		top += el.offsetTop;
		left += el.offsetLeft;
	}

	return (
		top >= window.pageYOffset &&
		left >= window.pageXOffset &&
		(top + height) <= (window.pageYOffset + window.innerHeight) &&
		(left + width) <= (window.pageXOffset + window.innerWidth)
	);
};

/**
 * Check wether an element is at least partially in viewport
 *
 * @param {Partial<HTMLElement>} el
 */
export const isElementPartiallyInViewport = (el) => {
	const width = el.offsetWidth;
	const height = el.offsetHeight;
	let top = el.offsetTop;
	let left = el.offsetLeft;

	while (el.offsetParent) {
		el = el.offsetParent;
		top += el.offsetTop;
		left += el.offsetLeft;
	}

	return (
		top < (window.pageYOffset + window.innerHeight) &&
		left < (window.pageXOffset + window.innerWidth) &&
		(top + height) > window.pageYOffset &&
		(left + width) > window.pageXOffset
	);
};

/**
 * @param {Element | HTMLElement | HTMLFormElement} element
 */
export const isElementDisabled = (element) => {
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return true;
	}

	if (element.classList.contains('disabled')) {
		return true;
	}

	// @ts-ignore: Property 'disabled' does not exist on type 'Element'
	if (typeof element.disabled !== 'undefined') {
		// @ts-ignore: Property 'disabled' does not exist on type 'Element'
		return element.disabled;
	}

	return element.hasAttribute('disabled') && element.getAttribute('disabled') !== 'false';
};

/**
 * @param {Element | HTMLElement} elem
 */
export const isElementDisplayed = elem => Boolean(elem['offsetWidth'] || elem['offsetHeight'] || elem.getClientRects().length > 0);

/**
 * @param {Element | HTMLElement} elem
 */
export const isElementVisible = elem => (isElementDisplayed(elem) && (window.getComputedStyle(elem).getPropertyValue('visibility') === 'visible'));

/**
 * @param {Element | HTMLElement} elem
 */
// export const isElementDetached = elem => !elem.closest('html');
export const isElementDetached = elem => (elem.isConnected === false);

/**
 * @param {HTMLElement} elem
 * @param {CallableFunction} onDetachCallback
 */
export const onDetachElement = (elem, onDetachCallback) => {
	const observer = new MutationObserver(() => {
		if (isElementDetached(elem)) {
			observer.disconnect();
			onDetachCallback();
		}
	});

	observer.observe(document, {
		childList: true,
		subtree: true,
	});
};

/**
 *
 * @param {string} componentType
 * @param {*} options
 * @param {JQuery.TriggeredEvent=} e
 */
export const applyComponentClickRestrictions = (componentType, options, e) => {

	// Don't do anything on mobile
	if (((options.mobile === false) || (options.disableMobile === true)) && (mpro.isMobileScreen() === true)) {
		return false;
	}

	// Don't do anything on desktop
	if (((options.desktop === false) || (options.disableDesktop === true)) && (mpro.isMobileScreen() === false)) {
		return false;
	}

	// Don't do anything if we have a guest
	if ((options.disableGuest === true) && mpro.isGuest()) {
		return false;
	}

	if (typeof e !== 'undefined') {
		// Continue as normal for cmd clicks etc
		if (e.which > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {

			// Exclude some components
			if (['confirm', 'alert', 'ask', 'dialog'].includes(componentType) === false) {

				/** @type {HTMLElement} */
				const element = e.currentTarget;
				let isClickable = false;

				if (
					// @ts-ignore
					// Anchor with href
					(element.href && !element.href.startsWith('#') && !element.href.startsWith('javascript')) ||
					element.dataset.url
				) {
					isClickable = true;
				}

				// Allow command clicks
				if (isClickable) {
					return false;
				}
			}
		}

		// Disable all other event handlers
		e.preventDefault();
		e.stopImmediatePropagation();
		e.stopPropagation();
	}

	return true;
};

/**
 * @param {string} text
 */
export const copyTextToClipboard = async (text) => {

	// Use the new navigator.clipboard API if available
	if (navigator.clipboard) {
		try {
			await navigator.clipboard.writeText(text);
		}
		catch (error) {
			console.error('Failed to copy text to clipboard', error);
			mpro.toast('error', {
				title: 'Failed to copy text to clipboard',
				content: 'Please allow the browser to access the clipboard',
			});
		}
	}
	// Fallback to the old (deprecated) execCommand API
	else {
		const controller = document.createElement('textarea');
		controller.classList.add('ignore-focustrap');
		document.body.append(controller);
		// inp.style.width = 0;
		// inp.style.height = 0;
		// @ts-ignore
		controller.value = text;
		controller.select();
		document.execCommand('copy', false);
		controller.remove();
	}
};

/**
 * Copy text from target element
 *
 * @param {HTMLElement|Element} trigger
 */
export const copyTextFromElement = (trigger) => {

	const $trigger = $(trigger);
	const data = $trigger.data();

	/* get data from element data attr */
	const initialText = data.bsOriginalTitle;
	const copiedText = data.copied;

	copyTextToClipboard(data.copy);

	/* display new tooltip text and after revert to original text */
	$trigger.attr('data-bs-original-title', copiedText);
	$trigger.tooltip('show');

	setTimeout(() => {
		$trigger.attr('data-bs-original-title', initialText);
		$trigger.attr('title', initialText);
	}, 500);
};

/**
 * @param {HTMLElement | Document} element
 * @param {string} eventName
 * @param {CustomEventInit} eventOptions
 */
export const triggerEvent = (element, eventName, eventOptions = {}) => {
	const evt = new CustomEvent(eventName, {
		bubbles: false,
		cancelable: true,
		...eventOptions,
	});
	element.dispatchEvent(evt);
	return evt;
};

/**
 * @link https://stackoverflow.com/questions/2661818/javascript-get-xpath-of-a-node
 * @param {Element} element
 */
export const getXPathForElement = (element) => {
	const idx = (sib, name) => sib
		? idx(sib.previousElementSibling, name || sib.localName) + (sib.localName == name)
		: 1;
	const segs = elm => (!elm || elm.nodeType !== 1
		? ['']
		: (elm.id && document.getElementById(elm.id) === elm
			? [`id("${elm.id}")`]
			: [...segs(elm.parentNode), elm instanceof HTMLElement
				? `${elm.localName}[${idx(elm)}]`
				: `*[local-name() = "${elm.localName}"][${idx(elm)}]`]));
	return segs(element).join('/');
};

/**
 * @param {HTMLTemplateElement} templateElement
 */
export const getTemplateFragment = (templateElement) => {

	if (!templateElement.content) {
		return void 0;
	}

	return templateElement.content.cloneNode(true);
};

/**
 * @param {typeof import('../modules/components/base-component').default} plugin
 */
// export const defineJQueryPlugin = plugin => {
// 	$(() => {
// 		/** @type {string} */
// 		const name = plugin.NAME;
// 		/** @type {any} */
// 		const JQUERY_NO_CONFLICT = $.fn[name];
// 		/** @type {any} */
// 		$.fn[name] = plugin.jQueryInterface;
// 		$.fn[name].Constructor = plugin;
// 		$.fn[name].noConflict = () => {
// 			$.fn[name] = JQUERY_NO_CONFLICT;
// 			return plugin.jQueryInterface;
// 		};
// 	});
// };